import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';
import { query, mutate } from '../client';

// ============ Types ============

export interface Program {
  id: string;
  name: string;
  description?: string | null;
  cover_image?: string | null;
  is_active?: boolean | null;
  language: string;
}

export interface Enrollment {
  id: string;
  user_id: string;
  program_id: string;
  status: string;
  current_phase_id?: string | null;
  enrolled_at: string | null;
  started_at?: string | null;
  program?: Program | null;
}

export interface PhaseActivity {
  id: string;
  phase_id: string;
  activity_type: string;
  activity_id: string;
  order_index: number;
  is_required: boolean | null;
  title_override?: string | null;
}

export interface Phase {
  id: string;
  program_id: string;
  name: string;
  description?: string | null;
  order_index: number;
  duration_days?: number | null;
  phase_activities?: PhaseActivity[];
}

export interface ActivityCompletion {
  phase_activity_id: string;
  status: string;
  completed_at?: string | null;
}

// ============ Queries ============

export async function getActivePrograms(language: string, productId?: string): Promise<ApiResult<Program[]>> {
  let queryBuilder = supabase
    .from('programs')
    .select('id, name, description, cover_image, is_active, language')
    .eq('is_active', true)
    .eq('language', language);

  if (productId) {
    queryBuilder = queryBuilder.eq('product_id', productId);
  }

  return query(async () => queryBuilder.order('created_at', { ascending: true }));
}

export async function getActiveEnrollment(userId: string): Promise<ApiResult<Enrollment | null>> {
  const result = await supabase
    .from('user_program_enrollments')
    .select('*, program:programs(id, name, description, cover_image, language, is_active, product_id)')
    .eq('user_id', userId)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }
  
  return ok(result.data);
}

export async function getProgramPhases(programId: string): Promise<ApiResult<Phase[]>> {
  return query(async () =>
    supabase
      .from('program_phases')
      .select('*, phase_activities(*)')
      .eq('program_id', programId)
      .order('order_index', { ascending: true })
  );
}

export async function getUserCompletions(userId: string): Promise<ApiResult<ActivityCompletion[]>> {
  return query(async () =>
    supabase
      .from('user_activity_completions')
      .select('phase_activity_id, status, completed_at')
      .eq('user_id', userId)
  );
}

export async function getAvailableProgramForGroup(
  language: string,
  groupId?: string | null,
  productId?: string
): Promise<ApiResult<Program | null>> {
  let queryBuilder = supabase
    .from('programs')
    .select('id, name, description, cover_image, language')
    .eq('is_active', true)
    .eq('language', language);

  if (productId) {
    queryBuilder = queryBuilder.eq('product_id', productId);
  }

  if (groupId) {
    queryBuilder = queryBuilder.or(`group_id.eq.${groupId},group_id.is.null`);
  } else {
    queryBuilder = queryBuilder.is('group_id', null);
  }

  const result = await queryBuilder
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }

  return ok(result.data);
}

// ============ Mutations ============

export async function createEnrollment(
  userId: string, 
  programId: string
): Promise<ApiResult<Enrollment>> {
  return mutate(async () =>
    supabase
      .from('user_program_enrollments')
      .insert({
        user_id: userId,
        program_id: programId,
        status: 'active',
        enrolled_at: new Date().toISOString(),
      })
      .select('*, program:programs(id, name, description, cover_image)')
      .single()
  );
}

export async function updateEnrollmentStatus(
  enrollmentId: string, 
  status: 'active' | 'paused' | 'completed'
): Promise<ApiResult<null>> {
  // When reactivating, update created_at to reflect this is the most recently used enrollment
  const updateData: { status: string; created_at?: string } = { status };
  if (status === 'active') {
    updateData.created_at = new Date().toISOString();
  }
  
  const result = await supabase
    .from('user_program_enrollments')
    .update(updateData)
    .eq('id', enrollmentId);

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }
  
  return ok(null);
}

export async function switchProgram(
  userId: string,
  currentEnrollmentId: string | null,
  newProgramId: string
): Promise<ApiResult<Enrollment>> {
  // Pause ALL active enrollments for this user (not just the current one)
  await supabase
    .from('user_program_enrollments')
    .update({ status: 'paused' })
    .eq('user_id', userId)
    .eq('status', 'active');

  // Check for existing enrollment for the new program
  const { data: existing } = await supabase
    .from('user_program_enrollments')
    .select('*')
    .eq('user_id', userId)
    .eq('program_id', newProgramId)
    .maybeSingle();

  if (existing) {
    // Reactivate existing enrollment
    await updateEnrollmentStatus(existing.id, 'active');
    return ok(existing as Enrollment);
  }

  // Create new enrollment
  return createEnrollment(userId, newProgramId);
}

export async function markActivityComplete(
  userId: string,
  phaseActivityId: string
): Promise<ApiResult<null>> {
  const result = await supabase
    .from('user_activity_completions')
    .upsert({
      user_id: userId,
      phase_activity_id: phaseActivityId,
      status: 'completed',
      completed_at: new Date().toISOString(),
    }, {
      onConflict: 'user_id,phase_activity_id',
    });

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }
  
  return ok(null);
}

export async function markActivityStarted(
  userId: string,
  phaseActivityId: string
): Promise<ApiResult<null>> {
  const result = await supabase
    .from('user_activity_completions')
    .upsert({
      user_id: userId,
      phase_activity_id: phaseActivityId,
      status: 'in_progress',
      started_at: new Date().toISOString(),
    }, {
      onConflict: 'user_id,phase_activity_id',
    });

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }
  
  return ok(null);
}
